import { Button, Dialog, Message } from '@alifd/next';
import {
  Cascader,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Space,
  Typography,
} from 'antd';
import locale from 'antd/es/date-picker/locale/zh_TW';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import client from '@/lib/feathers';

import { SUBMIT_BUTTON_FIELD_STYLE } from './constants';

const { Item, useForm } = Form;
const { TextArea } = Input;

const financeCommunity = client.service('financeCommunity');
const accountingSubjects = client.service('accountingSubjects');

const tradeTypeOptions = [
  {
    value: 'income',
    label: '收入',
  },
  {
    value: 'expense',
    label: '支出',
  },
  {
    value: 'transfer',
    label: '轉帳',
  },
];

const tradeTypeTitles = {
  income: {
    creditTitle: '收入種類',
    debitTitle: '存入帳戶',
  },
  expense: {
    creditTitle: '支出帳戶',
    debitTitle: '支出種類',
  },
  transfer: {
    creditTitle: '轉出帳戶',
    debitTitle: '轉入帳戶',
  },
};

export default function UpdateRecordDialog({
  nestedSubjects,
  setIsUpdate,
  editingRecordValues,
  showUpdateRecordDialog,
  setShowUpdateRecordDialog,
  uploads,
}) {
  const [form] = useForm();
  const [tradeType, setTradeType] = useState('income');
  const [uploadPath, setUploadPath] = useState('');
  const [uploadData, setUploadData] = useState(null);
  const [attachment, setAttachment] = useState(null);

  const uploadRef = useRef(null);

  useEffect(() => {
    if (Object.keys(editingRecordValues).length > 0) {
      setAttachment(editingRecordValues.attachment || null);
      setTradeType(editingRecordValues.tradeType || 'income');
    }
    form.setFieldsValue(editingRecordValues);
  }, [editingRecordValues, form]);

  const CascadeSubjects = useMemo(() => {
    const subjects = [];
    nestedSubjects.forEach((subject) => {
      const children = [];
      if (subject.children) {
        subject.children.forEach((child) => {
          if (child.activated) {
            children.push({
              value: child._id,
              label: `${child.subjectCode} ${child.subjectName}`,
            });
          }
        });
      }
      if (subject.activated) {
        subjects.push({
          value: subject._id,
          label: `${subject.subjectCode} ${subject.subjectName}`,
          children,
        });
      }
    });
    switch (tradeType) {
      case 'income':
        return {
          debitList: subjects.filter(
            (subject) => subject.label.slice(0, 1) === '1'
          ),
          creditList: subjects.filter(
            (subject) => subject.label.slice(0, 1) === '4'
          ),
        };
      case 'expense':
        return {
          debitList: subjects.filter((subject) =>
            ['2', '6'].includes(subject.label.slice(0, 1))
          ),
          creditList: subjects.filter(
            (subject) => subject.label.slice(0, 1) === '1'
          ),
        };
      case 'transfer':
        return {
          debitList: subjects.filter(
            (subject) => subject.label.slice(0, 1) === '1'
          ),
          creditList: subjects.filter(
            (subject) => subject.label.slice(0, 1) === '1'
          ),
        };
      default:
        break;
    }
  }, [nestedSubjects, tradeType]);

  const closeDialog = () => {
    setUploadPath('');
    setAttachment(null);
    setUploadData(null);
    setShowUpdateRecordDialog(false);
  };

  const handleUpdateTradeRecord = async (value) => {
    const {
      tradeDate,
      amount,
      remark,
      creditSubjectIdArray,
      debitSubjectIdArray,
    } = value;
    const tradeRecord = {
      tradeDate: tradeDate.valueOf(),
      tradeType,
      creditSubjectId: creditSubjectIdArray.at(-1),
      debitSubjectId: debitSubjectIdArray.at(-1),
      amount,
      remark,
    };
    try {
      let nowAttachment = attachment;
      const body = new FormData();
      body.append('files', uploadData);
      if (uploadData) {
        if (uploadData.size / 1024 / 1024 > 20) {
          return Message.error('上傳檔案不能超過20MB');
        }
        if (attachment) {
          await uploads.remove(attachment.id);
        }
        const res = await fetch('/newUploads?type=financeCommunity', {
          method: 'POST',
          body,
          headers: { authorization: window.localStorage['feathers-jwt'] },
        });

        const json = await res.json();
        const { showPath, _id } = json[0];

        nowAttachment = {
          path: showPath,
          name: uploadData.name,
          id: _id,
        };
      }

      if (
        editingRecordValues.attachment !== attachment &&
        attachment === null
      ) {
        await uploads.remove(editingRecordValues.attachment.id);
      }

      tradeRecord.attachment = nowAttachment;

      await financeCommunity.update(editingRecordValues._id, tradeRecord);

      await accountingSubjects.patch(creditSubjectIdArray.at(-1), {
        used: true,
      });
      await accountingSubjects.patch(debitSubjectIdArray.at(-1), {
        used: true,
      });
      closeDialog();
      setIsUpdate(true);
      Message.success('編輯交易成功');
    } catch (error) {
      Message.error('編輯交易失敗');
      console.error({ error });
    }
  };

  return (
    <Dialog
      visible={showUpdateRecordDialog}
      onClose={closeDialog}
      footer={false}
      hasMask
    >
      <Space direction="vertical" style={{ marginTop: '1rem', width: '400px' }}>
        <Form
          form={form}
          style={{ width: '400px' }}
          onFinish={handleUpdateTradeRecord}
        >
          <Item
            label="交易類型"
            style={{ marginLeft: '11.086px' }}
            name="tradeType"
          >
            <Radio.Group
              options={tradeTypeOptions}
              onChange={(e) => {
                setTradeType(e.target.value);
                if (e.target.value === editingRecordValues.tradeType) {
                  return form.setFieldsValue({
                    creditSubjectIdArray:
                      editingRecordValues.creditSubjectIdArray,
                    debitSubjectIdArray:
                      editingRecordValues.debitSubjectIdArray,
                  });
                } else {
                  return form.setFieldsValue({
                    creditSubjectIdArray: [],
                    debitSubjectIdArray: [],
                  });
                }
              }}
              optionType="button"
            />
          </Item>
          <Item
            name="tradeDate"
            label="交易日期"
            rules={[{ required: true, message: '請輸入交易日期' }]}
          >
            <DatePicker locale={locale} />
          </Item>
          <Item
            name="creditSubjectIdArray"
            label={tradeTypeTitles[tradeType].creditTitle}
            rules={[
              {
                required: true,
                message: `請輸入${tradeTypeTitles[tradeType].creditTitle}`,
              },
            ]}
          >
            <Cascader
              options={CascadeSubjects.creditList}
              expandTrigger="hover"
            />
          </Item>
          <Item
            name="amount"
            label="交易金額"
            rules={[{ required: true, message: '請輸入交易金額' }]}
          >
            <InputNumber min={1} />
          </Item>
          <Item
            name="debitSubjectIdArray"
            label={tradeTypeTitles[tradeType].debitTitle}
            rules={[
              {
                required: true,
                message: `請輸入${tradeTypeTitles[tradeType].debitTitle}`,
              },
            ]}
          >
            <Cascader
              options={CascadeSubjects.debitList}
              expandTrigger="hover"
            />
          </Item>
          <Item
            name="remark"
            label="交易內容"
            rules={[{ required: true, message: '請輸入交易內容' }]}
          >
            <TextArea rows={3} />
          </Item>
          <Item
            label="上傳附件"
            style={{
              marginLeft: '11.086px',
              height: '60px',
              marginTop: '1rem',
            }}
          >
            <Space wrap>
              <Button
                onClick={() => {
                  uploadRef.current.click();
                }}
              >
                {attachment ? '更換附件' : '上傳附件'}
              </Button>
              {uploadData && (
                <>
                  <Typography.Text type="secondary">
                    {uploadData.name}
                  </Typography.Text>
                  <Button
                    text
                    onClick={() => {
                      setUploadData(null);
                      setUploadPath('');
                    }}
                    style={{ opacity: 0.45 }}
                  >
                    清除
                  </Button>
                </>
              )}
              {!uploadData && attachment && (
                <>
                  <Typography.Text type="secondary">
                    {attachment.name}
                  </Typography.Text>
                  <Button
                    text
                    onClick={() => {
                      setAttachment(null);
                    }}
                    style={{ opacity: 0.45 }}
                  >
                    清除
                  </Button>
                </>
              )}
              {}
            </Space>
            <input
              ref={uploadRef}
              type="file"
              id="upload"
              value={uploadPath || ''}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0];
                setUploadData(file);
                setUploadPath(e.target.value);
              }}
            />
          </Item>
          <Item style={SUBMIT_BUTTON_FIELD_STYLE}>
            <Space>
              <Button type="primary" htmlType="submit">
                確定
              </Button>
            </Space>
          </Item>
        </Form>
      </Space>
    </Dialog>
  );
}
